import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantChannel } from '@arcnet/contracts';
import type {
  ChannelConnect, ChannelConnection, ChannelUpdate, Conversation, ConversationClose, ConversationMessage,
} from '@arcnet/contracts';
import { api } from './client.js';
import { useChannels } from './realtime.js';

/** WhatsApp and Telegram: the account's channels and its conversations (API modules/conversations, docs/43). */

export const conversationKeys = {
  channels: (t: string) => ['channels', t] as const,
  list: (t: string, scope: 'mine' | 'all', state: 'open' | 'closed') => ['conversations', t, scope, state] as const,
  one: (t: string, id: string) => ['conversations', t, 'one', id] as const,
};

export const useChannelConnections = (tenantId: string) =>
  useQuery({
    queryKey: conversationKeys.channels(tenantId),
    queryFn: () => api.get<{ data: ChannelConnection[] }>(`/v1/tenants/${tenantId}/channels`),
  });

export function useChannelMutations(tenantId: string) {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: conversationKeys.channels(tenantId) });
  return {
    connect: useMutation({
      mutationFn: (body: ChannelConnect) => api.post<ChannelConnection>(`/v1/tenants/${tenantId}/channels`, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; body: ChannelUpdate }) => api.patch<ChannelConnection>(`/v1/tenants/${tenantId}/channels/${v.id}`, v.body),
      onSuccess: done,
    }),
    disconnect: useMutation({
      mutationFn: (id: string) => api.delete(`/v1/tenants/${tenantId}/channels/${id}`),
      onSuccess: done,
    }),
  };
}

/**
 * The account's conversations, mine or all, kept current: the API nudges `conversations` on the
 * account's live connection whenever one arrives, is offered, taken or closed, or a message comes.
 */
export function useConversations(tenantId: string, scope: 'mine' | 'all', state: 'open' | 'closed' = 'open', enabled = true) {
  const qc = useQueryClient();
  useChannels(enabled ? [tenantChannel(tenantId, 'conversations')] : [], (msg) => {
    if (msg.type === 'nudge') void qc.invalidateQueries({ queryKey: ['conversations', tenantId] });
  });
  return useQuery({
    queryKey: conversationKeys.list(tenantId, scope, state),
    queryFn: () => api.get<{ data: Conversation[] }>(`/v1/tenants/${tenantId}/conversations?scope=${scope}&state=${state}`),
    enabled,
    // A safety net only; the nudge is what keeps it current.
    refetchInterval: 60_000,
  });
}

export const useConversation = (tenantId: string, id: string | null) =>
  useQuery({
    queryKey: conversationKeys.one(tenantId, id ?? ''),
    queryFn: () => api.get<{ conversation: Conversation; messages: ConversationMessage[] }>(`/v1/tenants/${tenantId}/conversations/${id}`),
    enabled: !!id,
  });

export function useConversationActions(tenantId: string) {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ['conversations', tenantId] });
  const base = (id: string) => `/v1/tenants/${tenantId}/conversations/${id}`;
  return {
    accept: useMutation({ mutationFn: (id: string) => api.post(`${base(id)}/accept`, {}), onSuccess: done }),
    pass: useMutation({ mutationFn: (id: string) => api.post(`${base(id)}/pass`, {}), onSuccess: done }),
    reply: useMutation({
      mutationFn: (v: { id: string; body: string }) => api.post(`${base(v.id)}/reply`, { body: v.body }),
      onSettled: done,
    }),
    close: useMutation({
      mutationFn: (v: { id: string; body: ConversationClose }) => api.post(`${base(v.id)}/close`, v.body),
      onSuccess: done,
    }),
  };
}
